/**
 * The one gesture behind all three intervention levers.
 *
 * Every lever in My Pipeline — set a status, adjust a quote's conversion, set
 * when it is expected to convert, adjust a client's unseen projection — ends in
 * the same place: a value and a one-line note, applied immediately. There is no approval step; the log and
 * the material-move alert are the control (product brief §6, "Bounded
 * authority"). Making it one dialog is what keeps the weekly pass to minutes:
 * the rep learns one interaction, not three.
 *
 * Two bounds are structural rather than advisory:
 *  - Conversion is (0, 100]. A zero gesture is NOT accepted as an override —
 *    zero conviction is a lost estimate, so the dialog converts itself into the
 *    lost-status flow, carrying the reason across. A 0% override would hide the
 *    loss from the decided cohort and quietly corrupt the win-rate calibration.
 *  - Lost requires a CATEGORY chosen from Tharstern's own configured statuses.
 *    It is not free text, because that string is written to Tharstern's
 *    StatusRef, and the dbt decode that turns StatusRef back into the `lost`
 *    cohort matches on known values — a typed-in phrase would decode to
 *    nothing and silently leave the quote counted as open.
 *
 * The category and the note are therefore two different fields, not one.
 * The category is controlled vocabulary bound for Tharstern; the note is the
 * rep's own sentence, kept in the intervention log, where free text is safe and
 * is the thing a manager actually reads. Marking won is deliberately absent:
 * that status is set by converting the estimate to a job, not by a rep here.
 */
import { useEffect, useState } from "react";
import {
  formatCurrencyGbpCompact,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  MAX_CONVERSION_OVERRIDE,
  MIN_CONVERSION_OVERRIDE,
  isValidConversionOverride,
} from "@shared-core/domain/win-rate-curve";
import { Button } from "../../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../primitives/dialog";
import { Input } from "../../../primitives/input";
import { Label } from "../../../primitives/label";
import type { InterventionImpact } from "@shared-core/domain/my-pipeline-interventions";
import { MATERIAL_MOVE_PCT } from "@shared-core/domain/my-pipeline-interventions";

/** Which lever the rep pulled. */
export type InterventionKind =
  | { kind: "status"; target: "lost" | "still_live" }
  | { kind: "conversion" }
  | { kind: "unseen" }
  | { kind: "expected_date" };

export interface InterventionRequest {
  lever: InterventionKind;
  /**
   * The quote this was raised on. Load-bearing: a reference is the Tharstern
   * HEADER ref, which several estimates share when one enquiry was quoted as
   * separate products, so resolving the target by label would apply the lever
   * to every sibling. Absent only for the client-level unseen lever and the
   * bulk bar, which carry their own targets.
   */
  quoteId?: string;
  /** Quote reference or client name — whatever the rep is acting on. */
  subjectLabel: string;
  /** Secondary context line, e.g. the quote description. */
  subjectDetail?: string;
  /** Current conversion share (0–1), for the conversion lever. */
  currentShare?: number;
  /** Current £ level, for the unseen lever. */
  currentValue?: number;
  /** Current effective date (ISO), for the expected-date lever. */
  currentDate?: string;
  /** What the system derives, so the dialog can show what a revert restores. */
  derivedDate?: string;
  /** The bucket's measured quote-to-order median, in working days. */
  derivedMedianWd?: number;
  /** System baseline £, shown so the rep sees what they are departing from. */
  baselineValue?: number;
  /** For the impact line, e.g. "August 2026". */
  monthLabel?: string;
  /** How many quotes this applies to, when the rep selected several. */
  bulkCount?: number;
}

export interface InterventionResult {
  lever: InterventionKind;
  /** New conversion share (0–1) or new £ level, depending on the lever. */
  value: number | null;
  /** New expected conversion date (ISO), for the expected-date lever. */
  date: string | null;
  /**
   * The discrete lost category, written to Tharstern's StatusRef. Null for
   * every lever except lost — nothing else writes back to Tharstern.
   */
  reasonCode: string | null;
  /** The rep's own line. Recorded in the intervention log only. */
  note: string;
}

const NOTE_MAX = 120;

export function InterventionDialog({
  request,
  lostReasons,
  preview,
  onApply,
  onClose,
}: {
  request: InterventionRequest | null;
  lostReasons: readonly string[];
  /**
   * What the draft would do to the month, recomputed as the rep types. A lever
   * whose effect is only visible after you pull it teaches people to pull it
   * less; this is the difference between an instrument and a lever in the dark.
   */
  preview?: (draft: InterventionResult) => InterventionImpact | null;
  onApply: (result: InterventionResult) => void;
  onClose: () => void;
}) {
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [numeric, setNumeric] = useState("");
  const [dateValue, setDateValue] = useState("");
  // Local, because a zero-conversion gesture rewrites which lever this is
  // mid-flow rather than rejecting the rep's input and making them start again.
  const [lever, setLever] = useState<InterventionKind | null>(null);

  useEffect(() => {
    if (!request) return;
    setLever(request.lever);
    setReasonCode(null);
    setNote("");
    setDateValue(request.currentDate ?? "");
    if (request.lever.kind === "conversion") {
      setNumeric(
        request.currentShare != null
          ? String(Math.round(request.currentShare * 100))
          : "",
      );
    } else if (request.lever.kind === "unseen") {
      setNumeric(
        request.currentValue != null ? String(request.currentValue) : "",
      );
    } else {
      setNumeric("");
    }
  }, [request]);

  if (!request || !lever) return null;

  // Captured after the guard: the hoisted `apply` / `convertToLost` bodies
  // below would otherwise see the un-narrowed `InterventionKind | null`.
  const activeLever = lever;
  const parsed = Number(numeric);
  const isConversion = activeLever.kind === "conversion";
  const isUnseen = activeLever.kind === "unseen";
  const isDate = activeLever.kind === "expected_date";
  const isLost = activeLever.kind === "status" && activeLever.target === "lost";

  // The zero gesture: not an error, a different intervention.
  const zeroGesture = isConversion && numeric.trim() !== "" && parsed === 0;

  const numericValid = isConversion
    ? isValidConversionOverride(parsed / 100)
    : isUnseen
      ? Number.isFinite(parsed) && parsed >= 0
      : isDate
        ? dateValue.trim() !== ""
        : true;

  // Lost is valid on a category alone — the category is the reason, so the
  // commonest hygiene gesture stays two clicks. The levers that move a number
  // require the rep's sentence, because the log is the only control on them.
  const canApply =
    numericValid &&
    note.length <= NOTE_MAX &&
    (isLost ? reasonCode !== null : note.trim().length > 0);

  function convertToLost() {
    setLever({ kind: "status", target: "lost" });
    setNumeric("");
  }

  const draft: InterventionResult = {
    lever: activeLever,
    value: isConversion ? parsed / 100 : isUnseen ? parsed : null,
    date: isDate ? dateValue : null,
    reasonCode,
    note: note.trim(),
  };
  const impact = numericValid && preview ? preview(draft) : null;

  function apply() {
    onApply({
      lever: activeLever,
      value: isConversion ? parsed / 100 : isUnseen ? parsed : null,
      date: isDate ? dateValue : null,
      reasonCode,
      note: note.trim(),
    });
    onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {titleFor(activeLever)}
            {request.bulkCount && request.bulkCount > 1
              ? ` — ${request.bulkCount} quotes`
              : ""}
          </DialogTitle>
          <DialogDescription>
            {request.subjectLabel}
            {request.subjectDetail ? ` · ${request.subjectDetail}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isConversion && !zeroGesture && (
            <div className="space-y-1.5">
              <Label htmlFor="intervention-value">Conversion %</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="intervention-value"
                  type="number"
                  min={MIN_CONVERSION_OVERRIDE * 100}
                  max={MAX_CONVERSION_OVERRIDE * 100}
                  value={numeric}
                  onChange={(e) => setNumeric(e.target.value)}
                  className="w-28"
                  aria-invalid={!numericValid && numeric.trim() !== ""}
                  aria-describedby="conversion-help"
                />
                <span className="text-sm text-muted-foreground">
                  replaces the curve&rsquo;s{" "}
                  {Math.round((request.currentShare ?? 0) * 100)}%
                </span>
              </div>
              <p id="conversion-help" className="text-xs text-muted-foreground">
                1&ndash;100. Setting 0 records the quote as lost instead.
              </p>
            </div>
          )}

          {zeroGesture && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
              <p className="font-medium">Zero conviction is a lost quote.</p>
              <p className="mt-1 text-muted-foreground">
                We&rsquo;ll record this as lost in Tharstern rather than a 0%
                override, so the win-rate calibration still sees the loss.
              </p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="mt-2"
                onClick={convertToLost}
              >
                Mark as lost instead
              </Button>
            </div>
          )}

          {isUnseen && (
            <div className="space-y-1.5">
              <Label htmlFor="intervention-value">
                Projected unseen work for this month
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">£</span>
                <Input
                  id="intervention-value"
                  type="number"
                  min={0}
                  step={1000}
                  value={numeric}
                  onChange={(e) => setNumeric(e.target.value)}
                  className="w-40"
                  aria-invalid={!numericValid && numeric.trim() !== ""}
                />
              </div>
              {request.baselineValue != null && (
                <p className="text-xs text-muted-foreground">
                  System baseline is{" "}
                  {formatCurrencyGbpCompact(request.baselineValue)}. Your figure
                  replaces it until month end.
                </p>
              )}
            </div>
          )}

          {isDate && (
            <div className="space-y-1.5">
              <Label htmlFor="intervention-date">Expected to convert on</Label>
              <Input
                id="intervention-date"
                type="date"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
                className="w-48"
                aria-invalid={!numericValid && dateValue.trim() !== ""}
                aria-describedby="date-help"
              />
              <p id="date-help" className="text-xs text-muted-foreground">
                {request.derivedMedianWd != null && request.derivedDate ? (
                  <>
                    Worked out as {formatShortDateOrDash(request.derivedDate)}{" "}
                    &mdash; this kind of work usually converts{" "}
                    {request.derivedMedianWd} working day
                    {request.derivedMedianWd === 1 ? "" : "s"} after quoting.
                    Your date replaces that until it converts.
                  </>
                ) : (
                  <>Your date replaces the worked-out one until it converts.</>
                )}
              </p>
            </div>
          )}

          {isLost && (
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <div className="flex flex-wrap gap-1.5">
                {lostReasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={reasonCode === r}
                    onClick={() => setReasonCode(r)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      reasonCode === r
                        ? "border-primary bg-primary/10 font-medium text-primary"
                        : "border-border text-muted-foreground hover:bg-surface-hover"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Written to the estimate&rsquo;s status in Tharstern. Pick the
                closest &mdash; add anything else in the note below.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="intervention-note">
              Note {isLost && <span className="font-normal">(optional)</span>}
            </Label>
            <Input
              id="intervention-note"
              value={note}
              maxLength={NOTE_MAX}
              placeholder={
                isLost
                  ? "Anything the category doesn't capture"
                  : "One line — what do you know that the system doesn't?"
              }
              onChange={(e) => setNote(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Recorded against your name in the intervention log.
            </p>
          </div>
        </div>

        {impact && (
          <ImpactLine impact={impact} monthLabel={request.monthLabel} />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canApply || zeroGesture} onClick={apply}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What this change does to the month, in the rep's terms.
 *
 * Leads with the delta rather than a before → after pair: at compact precision
 * a small change renders as "£204.4K to £204.4K", which reads as broken rather
 * than as small. A change too small to show is stated as such, and a change of
 * exactly nothing is worth saying out loud — clearing quotes the curve already
 * wrote off tidies the book without moving the forecast, and a rep who does not
 * know that will assume the page ignored them.
 */
function ImpactLine({
  impact,
  monthLabel,
}: {
  impact: InterventionImpact;
  monthLabel?: string;
}) {
  const month = monthLabel?.split(" ")[0] ?? "the month";
  const negligible = Math.abs(impact.delta) < NEGLIGIBLE_IMPACT_GBP;

  if (impact.delta === 0 || negligible) {
    return (
      <div className="rounded-md border border-border bg-surface-subtle px-3 py-2 text-sm text-muted-foreground">
        {impact.delta === 0 ? (
          <>
            This doesn&rsquo;t change {month} — these were already carrying
            nothing. It clears your book.
          </>
        ) : (
          <>Barely moves {month} (under £500).</>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border px-3 py-2 text-sm ${
        impact.isMaterial
          ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
          : "border-border bg-surface-subtle"
      }`}
    >
      <p>
        <strong
          className={`tabular-nums ${
            impact.delta > 0 ? "text-green-700 dark:text-green-400" : ""
          }`}
        >
          {impact.delta > 0 ? "+" : "−"}
          {formatCurrencyGbpCompact(Math.abs(impact.delta))}
        </strong>{" "}
        to {month}, leaving{" "}
        <span className="font-semibold tabular-nums">
          {formatCurrencyGbpCompact(impact.after)}
        </span>
        .
      </p>
      {impact.isMaterial && (
        <p className="mt-1 text-xs">
          {/* No leading article: "a 11%" / "an 8%" cannot both be right from
              one template, and the sentence reads better without one. */}
          {Math.round(impact.deltaPct * 100)}% move — over the{" "}
          {Math.round(MATERIAL_MOVE_PCT * 100)}% threshold, so your sales
          manager is notified. Still fine to do; just so you know.
        </p>
      )}
    </div>
  );
}

/** Below this a change is not worth a number; see ImpactLine. */
const NEGLIGIBLE_IMPACT_GBP = 500;

function titleFor(lever: InterventionKind): string {
  if (lever.kind === "expected_date") return "Set expected conversion date";
  if (lever.kind === "conversion") return "Adjust conversion";
  if (lever.kind === "unseen") return "Adjust projected unseen work";
  if (lever.target === "lost") return "Mark as lost";
  return "Confirm still live";
}
